import React from "react";
import { radarGeometry } from "../../../ForCoaches/v2/team/teamModel";
import { usePhoneTheme } from "../../useIsPhone";
import { TapPanel, openIndexIn, tapScopeOf, useTapPanel } from "../TapPanel";
import ChartTargets from "./ChartTargets";
import { CHART, FONT, GRID, PHONE_RADAR_WIDTH } from "./chartTokens";

// A percentile radar (the Player Archetype's five metrics, the team's six
// style axes): one spoke per axis clockwise from the top, the vertex at the
// percentile, round rings at 25 / 50 / 75 / 100, the shape in bm-orange.
// It generalises PercentileRadar and TeamStyleRadar on the shared
// teamModel.radarGeometry. Plain SVG (role="img" with a summary).
//
//   RadarPlot({ axes, ariaLabel, phone, renderDetail, onFocusVertex, onBlurVertex, tap, detailLabel })
//
// axes  [{ key, label, value, sublabel, ariaLabel }]: `label` is the code
//       ("PPP", "EFF"; words stack one per line), `value` the 0-100
//       percentile (null sits at the centre with a grey dot), `sublabel`
//       the line under the code ("Top 5%", "1.04 · 64"), `ariaLabel` the
//       vertex's full name ("Points per Possession: 1.11, Top 5%").
// Each vertex is a focusable target: on desktop hover or focus opens
//   renderDetail(index) in a HoverCard (below a vertex in the upper part,
//   above otherwise, aligned away from the centre); on phones a tap opens
//   it in a panel under the radar. onFocusVertex(i) / onBlurVertex(i) report
//   hover and focus so a card can mirror them. Pass `tap` (a useTapPanel())
//   to share the phone panel with rows of your own; the radar then leaves
//   the panel to you.
// Desktop is 440px wide (radius 108); phones 306px (radius 80, 13 / 11px
// labels), scaled down on narrow screens. `phone` overrides the theme.

const SIZES = {
  desktop: { width: 440, radius: 108, dot: 6, gap: 10, codeSize: 15, codeWeight: 700, codeLine: 19, codeBase: 15, subSize: 13, subLine: 17, subBase: 13, target: 30 },
  phone: { width: PHONE_RADAR_WIDTH, radius: 80, dot: 5, gap: 8, codeSize: 13, codeWeight: 600, codeLine: 18, codeBase: 13.7, subSize: 11, subLine: 16, subBase: 12, target: 32 },
};
const RINGS = [0.25, 0.5, 0.75, 1];
const PAD = 2;

const linesOf = (label) => String(label ?? "").split(" ").filter(Boolean);

// A label block beside its spoke's end: centred above the top spoke and
// below a bottom one, else beside the end, vertically centred on it.
const blockAt = (spoke, cx, size) => {
  const lines = linesOf(spoke.factor.label);
  const height = lines.length * size.codeLine + (spoke.factor.sublabel ? size.subLine : 0);
  const { x, y } = spoke.axis;
  if (Math.abs(x - cx) < 1) {
    return { lines, height, x, top: y < spoke.cy ? y - size.gap - height : y + size.gap, anchor: "middle" };
  }
  const right = x > cx;
  return { lines, height, x: right ? x + size.gap : x - size.gap, top: y - height / 2, anchor: right ? "start" : "end" };
};

// Laid out around cy = 0, then moved down so the highest label starts PAD
// from the top: the plot is as tall as its labels need.
export const radarLayout = (axes, size) => {
  const cx = size.width / 2;
  const order = axes.map((a, i) => String(a.key ?? i));
  const factors = axes.map((a, i) => ({ ...a, code: String(a.key ?? i), pct: a.value == null ? 0 : Math.max(0, Math.min(100, Number(a.value) || 0)) }));
  const spokesAt = (cy) => radarGeometry(factors, { cx, cy, radius: size.radius, order }).map((s) => ({ ...s, cy }));
  const first = spokesAt(0);
  const blocks0 = first.map((s) => blockAt(s, cx, size));
  const top = Math.min(-size.radius, ...blocks0.map((b) => b.top));
  const bottom = Math.max(size.radius, ...blocks0.map((b) => b.top + b.height));
  const cy = PAD - top;
  const spokes = spokesAt(cy);
  return { cx, cy, width: size.width, height: bottom - top + 2 * PAD, spokes, blocks: spokes.map((s) => blockAt(s, cx, size)) };
};

const vertexPlacement = (vertex, cx, cy) => {
  const side = vertex.y < cy - 30 ? "bottom" : "top";
  if (vertex.x > cx + 30) return `${side}-end`;
  if (vertex.x < cx - 30) return `${side}-start`;
  return side;
};

export const RadarPlot = ({ axes = [], ariaLabel, phone: phoneProp, renderDetail, onFocusVertex, onBlurVertex, tap: sharedTap, detailLabel = "Radar points" }) => {
  const themePhone = usePhoneTheme();
  const phone = phoneProp ?? themePhone;
  // New axes close the radar's own panel; a shared tap is the caller's.
  const ownTap = useTapPanel(tapScopeOf(axes));
  const tap = sharedTap || ownTap;
  if (!axes.length) return null;
  const size = phone ? SIZES.phone : SIZES.desktop;
  const { cx, cy, width, height, spokes, blocks } = radarLayout(axes, size);
  const summary = ariaLabel || axes.map((a) => [a.label, a.sublabel].filter(Boolean).join(" ")).join(", ");
  const targets = spokes.map((s, i) => ({
    key: s.code,
    label: axes[i].ariaLabel || [axes[i].label, axes[i].sublabel].filter(Boolean).join(", "),
    placement: vertexPlacement(s.vertex, cx, cy),
    style: {
      left: `calc(${(s.vertex.x / width) * 100}% - ${size.target / 2}px)`,
      top: `calc(${(s.vertex.y / height) * 100}% - ${size.target / 2}px)`,
      width: size.target,
      height: size.target,
    },
  }));
  const interactive = Boolean(renderDetail || onFocusVertex || sharedTap);
  const openIndex = openIndexIn(tap, axes.length);

  return (
    <div className="flex w-full flex-col items-center">
      <div className={phone ? "relative w-full max-w-[306px]" : "relative w-full max-w-[440px]"}>
        <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={summary} className="block h-auto w-full">
          {RINGS.map((r) => (
            <circle key={r} cx={cx} cy={cy} r={size.radius * r} fill="none" stroke={GRID} />
          ))}
          {spokes.map((s) => (
            <line key={`spoke-${s.code}`} x1={cx} y1={cy} x2={s.axis.x} y2={s.axis.y} stroke={GRID} />
          ))}
          <polygon
            points={spokes.map((s) => `${s.vertex.x},${s.vertex.y}`).join(" ")}
            fill={CHART.orange}
            fillOpacity={0.24}
            stroke={CHART.orange}
            strokeWidth={2.5}
            strokeLinejoin="round"
          />
          {spokes.map((s, i) => (
            <circle
              key={`dot-${s.code}`}
              cx={s.vertex.x}
              cy={s.vertex.y}
              r={openIndex === i ? size.dot + 1.5 : size.dot}
              fill={axes[i].value == null ? CHART.tertiary : CHART.orange}
              stroke={CHART.white}
              strokeWidth={2}
            />
          ))}
          {spokes.map((s, i) => {
            const b = blocks[i];
            return (
              <text key={`label-${s.code}`} x={b.x} textAnchor={b.anchor} aria-hidden="true">
                {b.lines.map((line, j) => (
                  <tspan
                    key={`${line}-${j}`}
                    x={b.x}
                    y={b.top + j * size.codeLine + size.codeBase}
                    fill={CHART.ink}
                    fontFamily={FONT.num}
                    fontSize={size.codeSize}
                    fontWeight={size.codeWeight}
                  >
                    {line}
                  </tspan>
                ))}
                {s.factor.sublabel && (
                  <tspan x={b.x} y={b.top + b.lines.length * size.codeLine + size.subBase} fill={CHART.muted} fontFamily={FONT.body} fontSize={size.subSize}>
                    {s.factor.sublabel}
                  </tspan>
                )}
              </text>
            );
          })}
        </svg>
        {interactive && (
          <ChartTargets
            targets={targets}
            renderDetail={renderDetail}
            phone={phone}
            tap={tap}
            label={detailLabel}
            shape="point"
            onFocusTarget={onFocusVertex}
            onBlurTarget={onBlurVertex}
          />
        )}
      </div>
      {phone && renderDetail && !sharedTap && (
        <TapPanel id={tap.panelId} open={openIndex !== null} onClose={tap.close} className="w-full">
          {openIndex !== null ? renderDetail(openIndex) : null}
        </TapPanel>
      )}
    </div>
  );
};

export default RadarPlot;
